import { useCallback, useEffect, useRef, useState } from "react";
import { extractTextFromAudio } from "../axios/uploadFiles";
import {
  playCancelSound,
  playHapticFeedback,
  playStartRecordingSound,
  playStopRecordingSound,
} from "../components/chat/whatsapp-audio-recorder/audio-effects";

const getAudioMimeType = (): string => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafari =
    (ua.includes("safari") && !ua.includes("chrome")) ||
    ua.includes("iphone") ||
    ua.includes("ipad");

  const mimeTypes = isSafari
    ? [
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/mpeg",
        "audio/webm;codecs=opus",
        "audio/webm",
      ]
    : [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/ogg",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
      ];

  for (const type of mimeTypes) {
    try {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    } catch {
      // Ignore unsupported mime type checks.
    }
  }

  return "";
};

const getBlobType = (recorderMimeType: string): string => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafari =
    (ua.includes("safari") && !ua.includes("chrome")) ||
    ua.includes("iphone") ||
    ua.includes("ipad");
  return isSafari ? "audio/mp3" : recorderMimeType || "audio/webm";
};

const formatRecordTime = (totalMs: number): string => {
  const totalSeconds = Math.floor(totalMs / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const hundredths = Math.floor((totalMs % 1000) / 10);
  return `${mins}:${secs < 10 ? "0" : ""}${secs},${hundredths < 10 ? "0" : ""}${hundredths}`;
};

export const formatVoiceClock = (totalMs: number): string => {
  const totalSeconds = Math.floor(totalMs / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

export const useVoiceToText = (
  onTranscribed: (text: string) => void,
  meta?: {
    assessment_id?: string | null;
    question_index?: number | null;
  },
) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [pausedPreviewUrl, setPausedPreviewUrl] = useState<string | null>(null);
  const [recordTimeMs, setRecordTimeMs] = useState(0);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const onTranscribedRef = useRef(onTranscribed);
  const extractMetaRef = useRef(meta);
  const recordedAudioUrlRef = useRef<string | null>(null);
  const pausedPreviewUrlRef = useRef<string | null>(null);
  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);
  const isTranscribingRef = useRef(false);
  const startInFlightRef = useRef(false);
  const cancelledRef = useRef(false);
  // Bumped to drop in-flight STT results after cancel / unmount / question change.
  const transcriptionEpochRef = useRef(0);
  const recordingStartedAtRef = useRef<number | null>(null);
  const accumulatedMsRef = useRef(0);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const invalidatePendingTranscription = useCallback(() => {
    transcriptionEpochRef.current += 1;
  }, []);

  useEffect(() => {
    onTranscribedRef.current = onTranscribed;
  }, [onTranscribed]);

  useEffect(() => {
    extractMetaRef.current = meta;
  }, [meta?.assessment_id, meta?.question_index]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    isTranscribingRef.current = isTranscribing;
  }, [isTranscribing]);

  const clearRecordTimer = useCallback(() => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    recordingStartedAtRef.current = null;
  }, []);

  const syncTimerDisplay = useCallback(() => {
    const running =
      recordingStartedAtRef.current != null
        ? Date.now() - recordingStartedAtRef.current
        : 0;
    setRecordTimeMs(accumulatedMsRef.current + running);
  }, []);

  const startRecordTimer = useCallback(
    (reset = true) => {
      clearRecordTimer();
      if (reset) {
        accumulatedMsRef.current = 0;
        setRecordTimeMs(0);
      }
      recordingStartedAtRef.current = Date.now();
      recordTimerRef.current = setInterval(syncTimerDisplay, 50);
    },
    [clearRecordTimer, syncTimerDisplay],
  );

  const pauseRecordTimer = useCallback(() => {
    if (recordingStartedAtRef.current != null) {
      accumulatedMsRef.current += Date.now() - recordingStartedAtRef.current;
      recordingStartedAtRef.current = null;
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecordTimeMs(accumulatedMsRef.current);
  }, []);

  const revokeRecordedUrl = useCallback(() => {
    if (recordedAudioUrlRef.current) {
      URL.revokeObjectURL(recordedAudioUrlRef.current);
      recordedAudioUrlRef.current = null;
      setRecordedAudioUrl(null);
    }
  }, []);

  const revokePausedPreview = useCallback(() => {
    if (pausedPreviewUrlRef.current) {
      URL.revokeObjectURL(pausedPreviewUrlRef.current);
      pausedPreviewUrlRef.current = null;
      setPausedPreviewUrl(null);
    }
  }, []);

  const setTranscribing = useCallback((value: boolean) => {
    isTranscribingRef.current = value;
    setIsTranscribing(value);
  }, []);

  const clearRecording = useCallback(() => {
    // Drop in-flight STT and any active recorder without cancel UX sounds —
    // used when locking an answer, dismissing preview, or leaving the field.
    invalidatePendingTranscription();
    cancelledRef.current = true;

    const state = mediaRecorderRef.current?.state;
    if (state === "recording" || state === "paused") {
      clearRecordTimer();
      accumulatedMsRef.current = 0;
      setRecordTimeMs(0);
      isPausedRef.current = false;
      setIsPaused(false);
      mediaRecorderRef.current?.stop();
    } else if (isTranscribingRef.current) {
      setTranscribing(false);
    }

    revokeRecordedUrl();
    revokePausedPreview();
  }, [
    clearRecordTimer,
    invalidatePendingTranscription,
    revokePausedPreview,
    revokeRecordedUrl,
    setTranscribing,
  ]);

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setMediaStream(null);
  }, []);

  const buildPreviewFromChunks = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || audioChunksRef.current.length === 0) return null;
    const blob = new Blob(audioChunksRef.current, {
      type: getBlobType(recorder.mimeType),
    });
    if (blob.size === 0) return null;
    return URL.createObjectURL(blob);
  }, []);

  const stopRecording = useCallback(() => {
    const state = mediaRecorderRef.current?.state;
    if (state !== "recording" && state !== "paused") {
      return;
    }

    setTranscribing(true);
    clearRecordTimer();
    revokePausedPreview();
    isPausedRef.current = false;
    setIsPaused(false);
    playStopRecordingSound();
    playHapticFeedback();
    mediaRecorderRef.current?.stop();
  }, [clearRecordTimer, revokePausedPreview, setTranscribing]);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    try {
      recorder.requestData();
    } catch {
      // ignore
    }
    recorder.pause();
    pauseRecordTimer();
    isPausedRef.current = true;
    setIsPaused(true);
    playHapticFeedback();

    // Allow the dataavailable flush to land before building preview.
    window.setTimeout(() => {
      if (!isPausedRef.current) return;
      revokePausedPreview();
      const previewUrl = buildPreviewFromChunks();
      if (previewUrl) {
        pausedPreviewUrlRef.current = previewUrl;
        setPausedPreviewUrl(previewUrl);
      }
    }, 80);
  }, [buildPreviewFromChunks, pauseRecordTimer, revokePausedPreview]);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "paused") return;

    revokePausedPreview();
    recorder.resume();
    isPausedRef.current = false;
    setIsPaused(false);
    startRecordTimer(false);
    playHapticFeedback();
  }, [revokePausedPreview, startRecordTimer]);

  const cancelRecording = useCallback(() => {
    const state = mediaRecorderRef.current?.state;
    const canCancel =
      isRecordingRef.current ||
      isTranscribingRef.current ||
      startInFlightRef.current ||
      state === "recording" ||
      state === "paused";

    if (!canCancel) {
      return;
    }

    cancelledRef.current = true;
    invalidatePendingTranscription();
    clearRecordTimer();
    accumulatedMsRef.current = 0;
    setRecordTimeMs(0);
    revokePausedPreview();
    revokeRecordedUrl();
    isPausedRef.current = false;
    setIsPaused(false);
    playCancelSound();
    playHapticFeedback();

    if (state === "recording" || state === "paused") {
      mediaRecorderRef.current?.stop();
      return;
    }

    cleanupStream();
    isRecordingRef.current = false;
    setIsRecording(false);
    setTranscribing(false);
    if (!startInFlightRef.current) {
      cancelledRef.current = false;
    }
  }, [
    cleanupStream,
    clearRecordTimer,
    invalidatePendingTranscription,
    revokePausedPreview,
    revokeRecordedUrl,
    setTranscribing,
  ]);

  const startRecording = useCallback(async () => {
    if (
      isTranscribingRef.current ||
      isRecordingRef.current ||
      startInFlightRef.current
    ) {
      return;
    }

    startInFlightRef.current = true;
    cancelledRef.current = false;

    try {
      revokeRecordedUrl();
      revokePausedPreview();
      isPausedRef.current = false;
      setIsPaused(false);

      playStartRecordingSound();
      playHapticFeedback();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      if (isTranscribingRef.current || cancelledRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        cancelledRef.current = false;
        return;
      }

      streamRef.current = stream;
      setMediaStream(stream);

      const mimeType = getAudioMimeType();
      const options: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const wasCancelled = cancelledRef.current;
        cancelledRef.current = false;
        clearRecordTimer();
        accumulatedMsRef.current = 0;
        isRecordingRef.current = false;
        isPausedRef.current = false;
        setIsRecording(false);
        setIsPaused(false);
        cleanupStream();
        revokePausedPreview();

        if (wasCancelled) {
          audioChunksRef.current = [];
          setRecordTimeMs(0);
          setTranscribing(false);
          return;
        }

        const blob = new Blob(audioChunksRef.current, {
          type: getBlobType(recorder.mimeType),
        });

        if (blob.size === 0) {
          setTranscribing(false);
          return;
        }

        const previewUrl = URL.createObjectURL(blob);
        recordedAudioUrlRef.current = previewUrl;
        setRecordedAudioUrl(previewUrl);
        setTranscribing(true);

        const epochAtStart = transcriptionEpochRef.current;
        try {
          const text = await extractTextFromAudio(blob, extractMetaRef.current);
          // Ignore stale results after unmount, Next question, or cancel.
          if (epochAtStart !== transcriptionEpochRef.current) {
            return;
          }
          if (text) {
            onTranscribedRef.current(text);
          }
        } catch (error) {
          console.error("Failed to transcribe audio:", error);
        } finally {
          if (epochAtStart === transcriptionEpochRef.current) {
            setTranscribing(false);
          }
        }
      };

      recorder.start(250);
      isRecordingRef.current = true;
      setIsRecording(true);
      startRecordTimer(true);
    } catch (error) {
      console.error("Cannot start voice recording:", error);
      cleanupStream();
      clearRecordTimer();
      accumulatedMsRef.current = 0;
      isRecordingRef.current = false;
      isPausedRef.current = false;
      setIsRecording(false);
      setIsPaused(false);
    } finally {
      startInFlightRef.current = false;
    }
  }, [
    cleanupStream,
    clearRecordTimer,
    revokePausedPreview,
    revokeRecordedUrl,
    setTranscribing,
    startRecordTimer,
  ]);

  const toggleRecording = useCallback(() => {
    if (isTranscribingRef.current || startInFlightRef.current) {
      return;
    }

    if (isPausedRef.current) {
      resumeRecording();
      return;
    }

    if (isRecordingRef.current) {
      stopRecording();
      return;
    }

    void startRecording();
  }, [resumeRecording, startRecording, stopRecording]);

  useEffect(() => {
    return () => {
      // Mark cancel BEFORE stop so onstop skips STT; also bump epoch so any
      // already-awaiting extractTextFromAudio cannot call onTranscribed after
      // this instance is gone (e.g. assessment Next question remount).
      cancelledRef.current = true;
      transcriptionEpochRef.current += 1;
      const state = mediaRecorderRef.current?.state;
      if (state === "recording" || state === "paused") {
        mediaRecorderRef.current?.stop();
      }
      clearRecordTimer();
      cleanupStream();
      revokeRecordedUrl();
      revokePausedPreview();
    };
  }, [
    cleanupStream,
    clearRecordTimer,
    revokePausedPreview,
    revokeRecordedUrl,
  ]);

  return {
    isRecording,
    isPaused,
    isTranscribing,
    recordedAudioUrl,
    pausedPreviewUrl,
    recordTimeMs,
    mediaStream,
    formattedRecordTime: formatRecordTime(recordTimeMs),
    formattedClockTime: formatVoiceClock(recordTimeMs),
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    toggleRecording,
    clearRecording,
  };
};
